import { DateTime } from 'luxon';

import { CreativeFile } from '@/common/models';
import Creative, { ICreativeAPI } from '@/common/models/creative/Creative';
import InventoryTargeting, { IInventoryTargetingAPI } from '@/common/models/InventoryTargeting';
import MediaOwnerApprovalStatus from '@/common/models/media-owner/MediaOwnerApprovalStatus.enum';
import { DASH, fromServerUtcDate, toServerDateTime } from '@/globals';
import i18n from '@/vueI18n';

export interface ICreativeMediaApprovalAPI {
  creative_mediaowner_approval_id: number;
  creative_id: number;
  creativefile_id: number;
  creative?: ICreativeAPI;
  approval_at: string;
  approval_comment: string;
  owner_id: number;
  state: string;
  submitted_at: string;
  network_id: number;
  approved_rejected_by: string;
  inventorytargeting_id?: number;
  inventory_targeting?: IInventoryTargetingAPI;
}

export default class CreativeMediaApproval {
  id: number;

  creativeID: number;

  creativefileID: number;

  creative: Creative | null;

  approvalAt: DateTime | null;

  approvalComment: string;

  ownerID: number;

  state: MediaOwnerApprovalStatus;

  submittedAt: DateTime | null;

  networkID: number;

  approvedRejectedBy: string;

  inventoryTargetingId: number;

  inventoryTargeting?: InventoryTargeting;

  constructor(
    id = -1,
    creativeID: number,
    creativefileID: number,
    creative: Creative,
    approvalAt: DateTime,
    approvalComment: string,
    ownerID: number,
    state: MediaOwnerApprovalStatus,
    submittedAt: DateTime,
    networkID = -1,
    approvedRejectedBy?: string,
    inventoryTargetingId?: number,
    inventoryTargeting?: InventoryTargeting,
  ) {
    this.id = id;
    this.creativeID = creativeID;
    this.creativefileID = creativefileID;
    this.creative = creative;
    this.approvalAt = approvalAt;
    this.approvalComment = approvalComment;
    this.ownerID = ownerID;
    this.state = state;
    this.submittedAt = submittedAt;
    this.networkID = networkID;
    this.approvedRejectedBy = approvedRejectedBy;
    this.inventoryTargetingId = inventoryTargetingId;
    this.inventoryTargeting = inventoryTargeting;
  }

  get hasComment(): boolean {
    return this.approvalComment !== null && this.approvalComment.trim() !== '';
  }

  get approvalAtFormatted(): string {
    return this.approvalAt !== null ? this.approvalAt.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get submittedOnFormatted(): string {
    return this.submittedAt !== null ? this.submittedAt.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  set creativeFiles(creativeFiles: CreativeFile[]) {
    this.creative.creativeFiles = creativeFiles;
  }

  get creativeFiles(): CreativeFile[] {
    return this.creative.creativeFiles;
  }

  get name(): string {
    return this.creative.name;
  }

  get status(): string {
    switch (this.state) {
      case MediaOwnerApprovalStatus.PENDING_APPROVAL:
        return i18n.global.t('states.pendingApproval');
      case MediaOwnerApprovalStatus.APPROVED:
        return i18n.global.t('states.approved');
      case MediaOwnerApprovalStatus.REJECTED:
        return i18n.global.t('states.rejected');
      default:
        return this.state;
    }
  }

  get isApprovedWithComplexApproval() {
    return this.networkID === -1 && this.inventoryTargetingId != null;
  }

  copy(): CreativeMediaApproval {
    return CreativeMediaApproval.fromApi(this.toApi());
  }

  static fromApi(apiObject: ICreativeMediaApprovalAPI): CreativeMediaApproval {
    return new CreativeMediaApproval(
      apiObject.creative_mediaowner_approval_id,
      apiObject.creative_id,
      apiObject.creativefile_id,
      apiObject.creative ? Creative.fromApi(apiObject.creative) : null,
      fromServerUtcDate(apiObject.approval_at),
      apiObject.approval_comment,
      apiObject.owner_id,
      apiObject.state as MediaOwnerApprovalStatus,
      fromServerUtcDate(apiObject.submitted_at),
      apiObject.network_id,
      apiObject.approved_rejected_by,
      apiObject.inventorytargeting_id,
      apiObject.inventory_targeting ? InventoryTargeting.fromAPI(apiObject.inventory_targeting) : null,
    );
  }

  toApi(): ICreativeMediaApprovalAPI {
    const obj: ICreativeMediaApprovalAPI = {
      creative_mediaowner_approval_id: this.id >= 0 ? this.id : undefined,
      creative_id: this.creativeID,
      creativefile_id: this.creativefileID,
      creative: this.creative != null ? this.creative.toApi() : undefined,
      approval_at: toServerDateTime(this.approvalAt || DateTime.local()),
      approval_comment: this.approvalComment,
      owner_id: this.ownerID,
      state: this.state,
      submitted_at: toServerDateTime(this.submittedAt || DateTime.local()),
      network_id: this.networkID,
      approved_rejected_by: this.approvedRejectedBy,
    };
    if (this.inventoryTargeting != null) {
      obj.inventorytargeting_id = this.inventoryTargetingId;
      obj.inventory_targeting = this.inventoryTargeting.toApi();
    }
    return obj;
  }
}
